/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "TECHNICIANS" TAB                      *
 *  File: apps/web/src/pages/bridge/TechniciansTab.js              *
 *  What it does: every Zoho person who hosts or rides along on a  *
 *  meeting, matched once to a person on the Dispatch roster. The  *
 *  bridge only suggests (same email, same name, same first name + *
 *  last initial); an owner confirms, picks someone else, or says  *
 *  "not on the roster". Each decision keeps who, when and why;    *
 *  the old match stays in the history ("show replaced").          *
 *  Reads: GET /techs.  Writes: confirm / unmatch / suggest again. *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — ONE ZOHO PERSON                                    *
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

// WHY (2026-10-13, James): "Yes, build it on those defaults" - decision 1: the
// Zoho Host is the technician; the match is a one-time, owner-confirmed table.
// Permissions stay on the HR Team page: this tab adds nobody to the roster.

import React, { useCallback, useEffect, useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS, TECH_STATUS_NAMES, TECH_HOW_NAMES } from './bridgeWords';
import { LoadState, WhyBox, ToneChip, formatWhen } from './bridgeParts';
import { fetchTechs, confirmTech, unmatchTech, suggestTechAgain } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — ONE ZOHO PERSON                                    *
 *****************************************************************/

// The chip each match status wears (word + icon + colour).
const STATUS_LOOKS = {
  proposed: { tone: 'info', icon: 'clock' },
  confirmed: { tone: 'ok', icon: 'check' },
  unmatched: { tone: 'neutral', icon: 'cross' },
};

/* ---- TechRow({ link, roster, onChanged }) ----------------------
   One Zoho person: who they are in Zoho, who they are in Butler,
   and the buttons to decide. A replaced (old) row is read-only. */
function TechRow({ link, roster, onChanged }) {
  const [openBox, setOpenBox] = useState('');                       // '', 'confirm', 'unmatch'
  const [pickedId, setPickedId] = useState(link.employeeId || '');  // the roster person chosen
  const [isBusy, setIsBusy] = useState(false);
  const [answerText, setAnswerText] = useState('');

  const isOld = Boolean(link.supersededAt);
  const look = STATUS_LOOKS[link.status] || STATUS_LOOKS.proposed;
  const activeRoster = roster.filter((onePerson) => onePerson.active !== false);
  const pickId = 'zb-tech-pick-' + link.id;

  /* ---- decide(whyText) -- confirm or unmatch, then reload ---- */
  async function decide(whyText) {
    setIsBusy(true);
    setAnswerText('');

    const answer = openBox === 'confirm'
      ? await confirmTech(link.zohoUserId, pickedId, whyText)
      : await unmatchTech(link.zohoUserId, whyText);

    setIsBusy(false);

    if (!answer.ok) {
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    setOpenBox('');
    onChanged();
  }

  /* ---- askAgain() -- suggest again from the roster ---- */
  async function askAgain() {
    setIsBusy(true);

    const answer = await suggestTechAgain(link.zohoUserId);

    setIsBusy(false);
    setAnswerText(answer.ok ? (answer.data.message || '') : (answer.message || BRIDGE_WORDS.loadFailed));
    onChanged();
  }

  return (
    <article className="zb-card" aria-labelledby={'zb-tech-' + link.id}>
      <header className="zb-card-head">
        <div className="zb-card-titles">
          <h3 className="zb-card-title" id={'zb-tech-' + link.id}>{link.zohoName || link.zohoUserId}</h3>
          <div className="zb-row zb-row-tight">
            <ToneChip tone={look.tone} icon={look.icon} word={TECH_STATUS_NAMES[link.status] || link.status} />
            {isOld ? <ToneChip tone="neutral" icon="history" word={BRIDGE_WORDS.replaced} /> : null}
          </div>
        </div>
      </header>

      <div className="zb-card-body">
        <dl className="zb-facts">
          <div className="zb-fact">
            <dt>{BRIDGE_WORDS.techsZohoPerson}</dt>
            <dd>{link.zohoName}{link.zohoEmail ? ' · ' + link.zohoEmail : ''}</dd>
          </div>
          <div className="zb-fact">
            <dt>{BRIDGE_WORDS.techsButlerPerson}</dt>
            <dd>
              {link.employeeName || '—'}
              {link.how ? ' (' + (link.status === 'proposed' ? BRIDGE_WORDS.techsSuggested + ': ' : '') + (TECH_HOW_NAMES[link.how] || link.how) + ')' : ''}
            </dd>
          </div>
        </dl>

        <p className="zb-small">
          {link.why ? '"' + link.why + '"' : ''}
          {link.decidedAt ? ' · ' + BRIDGE_WORDS.techsDecided + ' ' + formatWhen(link.decidedAt) : ''}
        </p>

        {answerText ? <div className="zb-notice zb-notice-info" role="status"><span>{answerText}</span></div> : null}
      </div>

      {!isOld && openBox === '' ? (
        <div className="zb-row">
          <label className="zb-field" htmlFor={pickId}>
            <span className="zb-label">{BRIDGE_WORDS.techsPick}</span>
            <select id={pickId} className="zb-select" value={pickedId} onChange={(event) => setPickedId(event.target.value)}>
              <option value="">{BRIDGE_WORDS.techsPick}</option>
              {activeRoster.map((onePerson) => (
                <option key={onePerson.id} value={onePerson.id}>{onePerson.name}{onePerson.email ? ' · ' + onePerson.email : ''}</option>
              ))}
            </select>
          </label>
          <button type="button" className="zb-button zb-button-primary" disabled={!pickedId} onClick={() => setOpenBox('confirm')}>
            <BridgeIcon name="check" />
            <span>{link.status === 'confirmed' && pickedId === link.employeeId ? BRIDGE_WORDS.techsChange : BRIDGE_WORDS.techsConfirm}</span>
          </button>
          {link.status !== 'unmatched' ? (
            <button type="button" className="zb-button zb-button-plain" onClick={() => setOpenBox('unmatch')}>
              <BridgeIcon name="cross" />
              <span>{BRIDGE_WORDS.techsNotOnRoster}</span>
            </button>
          ) : null}
          {link.status !== 'confirmed' ? (
            <button type="button" className="zb-button zb-button-plain" disabled={isBusy} onClick={askAgain}>
              <BridgeIcon name="history" />
              <span>{BRIDGE_WORDS.techsAskAgain}</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {!isOld && openBox !== '' ? (
        <WhyBox
          label={openBox === 'confirm' ? BRIDGE_WORDS.techsConfirmWhy : BRIDGE_WORDS.techsUnmatchWhy}
          confirmWord={openBox === 'confirm' ? BRIDGE_WORDS.techsConfirm : BRIDGE_WORDS.techsNotOnRoster}
          tone={openBox === 'confirm' ? 'primary' : 'danger'}
          busy={isBusy}
          onConfirm={decide}
          onCancel={() => setOpenBox('')}
        />
      ) : null}
    </article>
  );
}

/*****************************************************************
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

/* ---- TechniciansTab({ onChanged }) -- onChanged() refreshes the page counts ---- */
export default function TechniciansTab({ onChanged }) {
  const [links, setLinks] = useState([]);                // the matches
  const [roster, setRoster] = useState([]);              // the Dispatch roster
  const [rosterProblem, setRosterProblem] = useState(''); // why the roster is missing
  const [showOld, setShowOld] = useState(false);         // include replaced rows
  const [loadState, setLoadState] = useState('loading');

  /* ---- loadTechs(isQuiet) -- the matches and the roster ---- */
  const loadTechs = useCallback(async (isQuiet) => {
    if (!isQuiet) {
      setLoadState('loading');
    }

    const answer = await fetchTechs(showOld);

    if (!answer.ok) {
      setLoadState('error');
      return;
    }

    setLinks(answer.data.links || []);
    setRoster(answer.data.roster || []);
    setRosterProblem(answer.data.rosterProblem || '');
    setLoadState('ready');
  }, [showOld]);

  useEffect(() => {
    loadTechs(false);
  }, [loadTechs]);

  /* ---- afterChange() -- reload this tab and the page counts ---- */
  function afterChange() {
    loadTechs(true);
    onChanged();
  }

  // Waiting-for-you first, then the rest by name (the server already sorts by name).
  const ordered = links.slice().sort((first, second) => Number(second.status === 'proposed') - Number(first.status === 'proposed'));

  return (
    <section aria-labelledby="zb-tab-techs-title">
      <h2 id="zb-tab-techs-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabTechs}</h2>
      <p className="zb-quiet">{BRIDGE_WORDS.techsIntro}</p>
      <p className="zb-small">{BRIDGE_WORDS.techsAddOnHr}</p>

      {rosterProblem ? (
        <div className="zb-notice zb-notice-warn" role="status">
          <BridgeIcon name="alert" />
          <span>{BRIDGE_WORDS.techsRosterProblem}: {rosterProblem}</span>
        </div>
      ) : null}

      <div className="zb-filters">
        <label className="zb-check">
          <input type="checkbox" checked={showOld} onChange={(event) => setShowOld(event.target.checked)} />
          <span>{BRIDGE_WORDS.techsShowOld}</span>
        </label>
      </div>

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={() => loadTechs(false)} /> : null}

      {loadState === 'ready' && links.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.techsEmpty} /> : null}

      {loadState === 'ready' ? (
        <div className="zb-cards">
          {ordered.map((oneLink) => (
            <TechRow key={oneLink.id} link={oneLink} roster={roster} onChanged={afterChange} />
          ))}
        </div>
      ) : null}
    </section>
  );
}
